<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">ADD SO TRACKER</h1>
      </v-row>

      <!-- Add SO Card Button -->
      <v-row v-if="canEditSalesTracker" justify="center" class="mb-4">
        <v-btn
          large
          @click="navigateToAddSalesTracker"
          class="nexus-btn"
        >
          <v-icon left>mdi-plus</v-icon>
          Add SO Card
        </v-btn>
      </v-row>



      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" />
              </v-overlay>

              <!-- Section Header -->
              <div class="section-header mb-6">
                <h2 class="section-title">SO TRACKER DETAILS</h2>
                <div class="section-divider"></div>
              </div>

              <v-form ref="form" v-model="valid">
                <!-- Top Row: SO Number, Customer -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="salesOrderNumber"
                      label="SO Number"
                      dense outlined 
                      prepend-inner-icon="mdi-note-text"
                      :rules="[rules.required, rules.soNumber, rules.noDuplicate]"
                      @input="sanitizeSalesOrderNumber"
                      class="nexus-input"
                      :error-messages="duplicateError"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="customerName"
                      :items="customerOptions" 
                      item-text="companyName" 
                      item-value="docId"
                      label="Customer Name" 
                      dense outlined 
                      prepend-inner-icon="mdi-account-group"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Personnel Row -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="engineer" 
                      :items="engineerOptions" 
                      item-text="fullName" 
                      return-object
                      label="Engineer" 
                      dense outlined 
                      prepend-inner-icon="mdi-account"
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select
                      v-model="salesperson" 
                      :items="salespersonOptions" 
                      item-text="fullName" 
                      return-object
                      label="Salesperson" 
                      dense outlined 
                      prepend-inner-icon="mdi-account"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Dates and Status Row -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-menu ref="orderDateMenu" v-model="orderDateMenu" :close-on-content-click="false"
                            transition="scale-transition" offset-y min-width="auto">
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="orderPlacedDate" 
                          label="Order Placed Date" 
                          dense outlined readonly
                          prepend-inner-icon="mdi-calendar" 
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]"
                          class="nexus-input"
                        />
                      </template>
                      <v-date-picker v-model="orderPlacedDate" @input="orderDateMenu=false" />
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-select
                      v-model="method" 
                      :items="methodOptions" 
                      label="Method" 
                      dense outlined
                      prepend-inner-icon="mdi-truck-delivery" 
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-select
                      v-model="status" 
                      :items="statusOptions" 
                      label="Status" 
                      dense outlined
                      prepend-inner-icon="mdi-information" 
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-menu ref="arrivalDateMenu" v-model="arrivalDateMenu" :close-on-content-click="false"
                            transition="scale-transition" offset-y min-width="auto">
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="arrivalDate" 
                          label="Expected Arrival Date" 
                          dense outlined readonly
                          prepend-inner-icon="mdi-calendar" 
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]"
                          class="nexus-input"
                        />
                      </template>
                      <v-date-picker v-model="arrivalDate" @input="arrivalDateMenu=false" />
                    </v-menu>
                  </v-col>
                </v-row>

                <!-- PDF Upload Section -->
                <div class="section-header mb-4 mt-6">
                  <h3 class="section-title">SO PDF DOCUMENT</h3>
                  <div class="section-divider"></div>
                </div>
                <p class="mb-4 text-body-2" style="color: #64748b;">Upload the SO PDF containing all line items and specifications</p>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="pdfFile"
                      label="Upload SO PDF"
                      accept=".pdf"
                      prepend-icon="mdi-file-pdf-box"
                      outlined
                      dense
                      :rules="[rules.required]"
                      @change="handlePDFUpload"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-btn 
                      v-if="pdfUrl"
                      color="primary" 
                      @click="viewPDF"
                      class="mr-4 nexus-btn"
                    >
                      <v-icon left>mdi-eye</v-icon>Preview PDF
                    </v-btn>
                    <span v-if="pdfUrl" class="success--text">
                      <v-icon color="success" class="mr-1">mdi-check-circle</v-icon>
                      PDF Uploaded Successfully
                    </span>
                    <span v-else class="grey--text">No PDF uploaded</span>
                  </v-col>
                </v-row>

                <!-- Initial Procurement Update -->
                <div class="section-header mb-4 mt-6">
                  <h3 class="section-title">INITIAL PROCUREMENT UPDATE</h3>
                  <div class="section-divider"></div>
                </div>
                
                <v-row>
                  <v-col cols="12" md="8">
                    <v-textarea
                      v-model="latestUpdate" 
                      label="Add initial update about this SO" 
                      outlined dense
                      prepend-inner-icon="mdi-comment-text-outline" 
                      rows="3" auto-grow
                      placeholder="e.g., SO received from supplier, processing timeline, special requirements..."
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex align-center">
                    <v-btn 
                      color="primary" 
                      @click="addUpdate" 
                      :disabled="!latestUpdate"
                      class="nexus-btn"
                    >
                      <v-icon left>mdi-plus</v-icon>Add Update
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row justify="center" class="my-6">
                  <v-btn
                    large 
                    color="primary"
                    class="nexus-btn"
                    @click="confirmSubmit"
                    :disabled="saving"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ saving ? 'Creating...' : 'Create SO Tracker' }}
                  </v-btn>
                </v-row>
              </v-form>

              <!-- Shipment Info Table -->
              <v-row v-if="shipmentAuditTrail.length" class="mt-6">
                <v-col cols="12">
                  <div class="section-header mb-4">
                    <h3 class="section-title">
                      <v-icon color="#475569" class="mr-2">mdi-truck</v-icon>
                      SHIPMENT INFO
                    </h3>
                    <div class="section-divider"></div>
                  </div>
                  <v-data-table 
                    :headers="shipmentHeaders" 
                    :items="shipmentAuditTrail" 
                    class="nexus-table"
                    :items-per-page="10"
                  >
                    <template #item.linkedDate="{ item }">
                      {{ new Date(item.linkedDate).toLocaleString() }}
                    </template>
                    <template #item.status="{ item }">
                      <v-chip 
                        :color="getShipmentStatusColor(item.status)" 
                        small text-color="white"
                      >
                        {{ item.status }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>

              <!-- Updates Preview Table -->
              <v-row v-if="updates.length" class="mt-6">
                <v-col cols="12">
                  <div class="section-header mb-4">
                    <h3 class="section-title">UPDATE PREVIEW</h3>
                    <div class="section-divider"></div>
                  </div>
                  <v-data-table 
                    :headers="tableHeaders" 
                    :items="sortedUpdates" 
                    class="nexus-table"
                    :items-per-page="5"
                    :items-per-page-options="[5]"
                  >
                    <template #item.timestamp="{ item }">
                      {{ new Date(item.timestamp).toLocaleString() }}
                    </template>
                    <template #item.actions="{ item }">
                      <v-btn
                        icon color="error"
                        @click="removeUpdate(updates.indexOf(item))"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- PDF Viewer Dialog -->
    <v-dialog v-model="pdfDialog.visible" max-width="90%" max-height="90%">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">
          SO PDF Preview
          <v-spacer />
          <v-btn icon @click="pdfDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0" style="height: 80vh;">
          <iframe 
            v-if="pdfDialog.url"
            :src="pdfDialog.url" 
            width="100%" 
            height="100%"
            style="border: none;"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="success" text @click="confirmationDialog.confirm">Yes</v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible=false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" color="success" timeout="3000">
      SO Tracker created successfully!
      <v-btn color="white" text @click="snackbar=false">Close</v-btn>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar=false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, addDoc, getDocs, query, where, updateDoc, doc
} from 'firebase/firestore';
import { 
  getStorage, ref as storageRef, uploadBytes, getDownloadURL 
} from 'firebase/storage';
import { firebaseApp, auth } from '@/firebaseConfig';

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  name: 'AddSalesTracker',
  data() {
    return {
      loading: false, 
      valid: false,
      saving: false,

      // Core fields
      salesOrderNumber: '', 
      customerName: '',
      engineer: null, 
      salesperson: null,
      orderPlacedDate: '', 
      method: '', 
      status: 'Open Order',
      arrivalDate: '', 
      latestUpdate: '', 
      updates: [],

      // User permissions for access control
      userPermissions: {},

      // PDF handling
      pdfFile: null,
      pdfUrl: '',

      // Options
      customerOptions: [],
      engineerOptions: [], 
      salespersonOptions: [],
      methodOptions: ['Air', 'Sea'],
      statusOptions: [
        'Open Order', 'Referenced', 'Booked', 'Manufacturing', 
        'Partial Supply', 'In Transit', 'Docked', 'At Airport', 
        'On Route to JHB', 'Complete', 'Delivered'
      ],

      // Shipment tracking
      shipmentAuditTrail: [],

      // UI state
      orderDateMenu: false, 
      arrivalDateMenu: false,
      pdfDialog: {
        visible: false,
        url: ""
      },

      // Dialogs
      confirmationDialog: { visible: false, title: '', message: '', confirm: null },
      snackbar: false, 
      errorSnackbar: false, 
      errorMessage: '',

      // Validation
      duplicateError: '',
      rules: { 
        required: v => !!v || 'Required.',
        soNumber: v => /^\d+$/.test(v) || 'Only numbers allowed.',
        noDuplicate: () => !this.duplicateError || this.duplicateError
      },
      
      tableHeaders: [
        { text: 'Date & Time', value: 'timestamp' },
        { text: 'Update', value: 'text' },
        { text: 'User', value: 'user' },
        { text: '', value: 'actions', sortable: false }
      ],

      shipmentHeaders: [
        { text: 'Shipment #', value: 'shipmentNumber' },
        { text: 'Status', value: 'status' },
        { text: 'Linked Date', value: 'linkedDate' },
        { text: 'Note', value: 'note' }
      ],
      
      loggedInUser: '',
      loggedInUserName: ''
    };
  },

  computed: {
    sortedUpdates() {
      return this.updates.slice().sort((a, b) => b.timestamp - a.timestamp);
    },
    
    canEditSalesTracker() {
      return this.userPermissions.salesOrderTracker?.Add_Button || false;
    }
  },

  async created() {
    try {
      const user = auth.currentUser;
      if (user) {
        this.loggedInUser = user.email;
        await this.fetchUserName(user.email);
        await this.fetchUserPermissions(user.email);
      }
    } catch (e) { 
      // Error handled silently
    }

    await Promise.all([
      this.fetchCustomerOptions(),
      this.fetchEngineerOptions(),
      this.fetchSalespersonOptions(),
      this.fetchShipmentAuditTrail()
    ]);
  },

  methods: {
    async fetchUserPermissions(email) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          this.userPermissions = snap.docs[0].data().permissions || {};
        }
      } catch (e) {
        // Error handled silently
      }
    },

    async fetchUserName(email) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          this.loggedInUserName = snap.docs[0].data().fullName || email;
        } else {
          this.loggedInUserName = email;
        }
      } catch (e) {
        this.loggedInUserName = email;
      }
    },

    // Fetch reference data
    async fetchCustomerOptions() {
      try {
        const snap = await getDocs(collection(db, 'customers'));
        this.customerOptions = snap.docs.map(doc => ({
          docId: doc.id,
          companyName: doc.data()['Company Name'] || doc.data().customerName || '',
          contactPerson: doc.data()['Contact Person'] || '',
          customerAccount: doc.data()['Customer Account'] || ''
        })).filter(o => o.companyName);
      } catch (e) { 
        this.showError('Failed to fetch customers.'); 
      }
    },

    async fetchEngineerOptions() {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'Engineer'));
        const snap = await getDocs(q);
        this.engineerOptions = snap.docs.map(d => ({ 
          uid: d.id, 
          fullName: d.data().fullName 
        }));
      } catch (e) { 
        this.showError('Failed to fetch engineers.'); 
      }
    },

    async fetchSalespersonOptions() {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'Salesperson'));
        const snap = await getDocs(q);
        this.salespersonOptions = snap.docs.map(d => ({ 
          uid: d.id, 
          fullName: d.data().fullName 
        }));
      } catch (e) { 
        this.showError('Failed to fetch salespersons.'); 
      }
    },

    async fetchShipmentAuditTrail() {
      // For new SO creation, this will be empty initially
      this.shipmentAuditTrail = [];
    },

    getShipmentStatusColor(status) {
      const colorMap = {
        'Open Order': 'indigo',
        'In Transit': 'orange',
        'Delivered': 'success',
        'Referenced': 'blue'
      };
      return colorMap[status] || 'grey';
    },

    // SO Number validation
    sanitizeSalesOrderNumber() {
      // Remove "SO" prefix if present (case insensitive)
      this.salesOrderNumber = this.salesOrderNumber.replace(/^SO/i, '');
      // Remove any non-numeric characters
      this.salesOrderNumber = this.salesOrderNumber.replace(/\D/g, '');
      
      // Check for duplicates
      if (this.salesOrderNumber) {
        this.checkDuplicateSO();
      } else {
        this.duplicateError = '';
      }
    },

    async checkDuplicateSO() {
      try {
        const q = query(
          collection(db, 'SalesTracker'), 
          where('salesOrderNumber', '==', this.salesOrderNumber)
        );
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          this.duplicateError = 'SO number already exists.';
          this.valid = false;
        } else {
          this.duplicateError = '';
        }
      } catch (error) {
        // Handle silently
      }
    },

    // PDF handling
    async handlePDFUpload() {
      if (!this.pdfFile) return;
      
      try {
        this.loading = true;
        const fileName = `so_pdfs/${this.salesOrderNumber}_${Date.now()}.pdf`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, this.pdfFile);
        this.pdfUrl = await getDownloadURL(fileRef);
        
        this.showSuccess('PDF uploaded successfully!');
      } catch (error) {
        this.showError('Failed to upload PDF.');
      } finally {
        this.loading = false;
      }
    },

    viewPDF() {
      if (this.pdfUrl) {
        this.pdfDialog.url = this.pdfUrl;
        this.pdfDialog.visible = true;
      }
    },

    // Updates management
    addUpdate() {
      if (!this.latestUpdate) return;
      this.updates.push({
        timestamp: Date.now(), 
        text: this.latestUpdate, 
        user: this.loggedInUserName
      });
      this.latestUpdate = '';
    },

    removeUpdate(i) { 
      this.updates.splice(i, 1); 
    },

    // Form submission
    confirmSubmit() {
      // Validate form first
      if (!this.$refs.form.validate()) {
        this.showError("Please fill in all required fields correctly.");
        return;
      }
      
      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Create',
        message: 'Create this SO tracker entry?',
        confirm: this.submitForm
      };
    },

    async submitForm() {
      // Check for duplicate one final time before submitting
      await this.checkDuplicateSO();
      
      if (this.duplicateError || !this.$refs.form.validate() || this.saving) return;
      
      this.saving = true;
      this.loading = true;
      
      try {
        // Find the selected customer based on customerName (docId)
        const selectedCustomer = this.customerOptions.find(
          customer => customer.docId === this.customerName
        );

        const soData = {
          salesOrderNumber: this.salesOrderNumber, 
          customerName: selectedCustomer ? selectedCustomer.companyName : '',
          engineer: this.engineer, 
          salesperson: this.salesperson,
          orderPlacedDate: this.orderPlacedDate, 
          method: this.method, 
          status: this.status,
          arrivalDate: this.arrivalDate, 
          pdfUrl: this.pdfUrl,
          supplierUpdates: this.updates,
          createdDate: new Date().toISOString(),
          defaultShipment: "OPEN_ORDER",
          shipmentAuditTrail: [{
            shipmentNumber: 'OPEN_ORDER',
            status: 'Open Order',
            linkedDate: Date.now(),
            note: 'Initial auto-link to Open Order'
          }]
        };

        await addDoc(collection(db, 'SalesTracker'), soData);
        
        // Auto-link to Open Order shipment
        await this.linkSOToOpenOrder(this.salesOrderNumber);
        
        this.snackbar = true;
        
        // Reset form
        this.$refs.form.reset(); 
        this.updates = []; 
        this.pdfFile = null;
        this.pdfUrl = '';
        this.shipmentAuditTrail = [];
        this.duplicateError = '';
        
        // Navigate back after delay
        setTimeout(() => {
          this.$router.go(-1);
        }, 1500);
        
      } catch (e) { 
        this.showError('Failed to create SO tracker.'); 
      } finally { 
        this.loading = false; 
        this.saving = false;
        this.confirmationDialog.visible = false; 
      }
    },

    async linkSOToOpenOrder(soNumber) {
      try {
        // Find the Open Order shipment
        const openOrderQuery = query(
          collection(db, 'shipments'), 
          where('shipmentNumber', '==', 'OPEN_ORDER')
        );
        const snapshot = await getDocs(openOrderQuery);
        
        if (!snapshot.empty) {
          const openOrderDoc = snapshot.docs[0];
          const currentLinkedSOs = openOrderDoc.data().linkedSONumbers || [];
          
          // Add this SO to the Open Order shipment if not already there
          if (!currentLinkedSOs.includes(soNumber)) {
            await updateDoc(openOrderDoc.ref, {
              linkedSONumbers: [...currentLinkedSOs, soNumber]
            });
          }
        }
      } catch (error) {
        // Don't show error to user as SO was created successfully
      }
    },

    // Navigation
    navigateToAddSalesTracker() {
      this.$router.push('/add-sales-tracker');
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true, 
        title: 'Confirm Navigation',
        message: 'Go back and discard changes?', 
        confirm: this.goBack
      };
    },

    goBack() { 
      this.confirmationDialog.visible = false; 
      this.$router.go(-1); 
    },

    // Helper methods
    showError(msg) { 
      this.errorMessage = msg; 
      this.errorSnackbar = true; 
    },
    
    showSuccess(msg) {
      // Success messages handled via snackbar
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.nexus-table {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 12px !important;
  overflow: hidden !important;
}

.nexus-table >>> .v-data-table__wrapper {
  border-radius: 12px !important;
}

.nexus-table >>> th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #475569 !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

/* Form validation styles */
.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

/* Loading overlay */
.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

/* Responsive Design */
@media (max-width: 768px) {
  .nexus-title {
    font-size: 1.75rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
}
</style>